/* eslint-disable react/no-array-index-key */
import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AttributeDiff,
  TextDiffPart,
  computeLineDiff,
  formatValueForDiff,
} from '../use-version-diff';
import { DIFF_COLORS } from '../../constants';

interface DiffAccordionProps {
  diff: AttributeDiff;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: (key: string, isExpanded: boolean) => void;
}

interface DiffLineProps {
  part: TextDiffPart;
  lineNumber?: number;
  side: 'left' | 'right';
}

const DiffLine: React.FC<DiffLineProps> = ({ part, lineNumber, side }) => {
  const isAdded = part.added;
  const isRemoved = part.removed;

  let backgroundColor = 'transparent';
  let textColor = 'inherit';
  let prefix = ' ';

  if (isAdded) {
    backgroundColor = DIFF_COLORS.added.bg;
    textColor = DIFF_COLORS.added.text;
    prefix = '+';
  } else if (isRemoved) {
    backgroundColor = DIFF_COLORS.removed.bg;
    textColor = DIFF_COLORS.removed.text;
    prefix = '-';
  }

  // Only show removed on left, added on right
  if ((side === 'left' && isAdded) || (side === 'right' && isRemoved)) {
    return null;
  }

  // Keep all lines except trailing empty line
  const lines = part.value.split('\n').filter((line, idx, arr) => idx < arr.length - 1 || line !== '');

  if (lines.length === 0) return null;

  return (
    <>
      {lines.map((line, idx) => (
        <Box
          key={`${lineNumber}-${idx}`}
          sx={{
            display: 'flex',
            backgroundColor,
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: '20px',
            minHeight: '20px',
          }}
        >
          <Box
            sx={{
              width: '24px',
              minWidth: '24px',
              backgroundColor: 'grey.100',
              color: 'text.secondary',
              textAlign: 'center',
              borderRight: 1,
              borderColor: 'divider',
              userSelect: 'none',
            }}
          >
            {prefix}
          </Box>
          <Box
            sx={{
              flex: 1,
              px: 1,
              color: textColor,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {line || ' '}
          </Box>
        </Box>
      ))}
    </>
  );
};

interface SideBySideDiffProps {
  oldValue: unknown;
  newValue: unknown;
}

const SideBySideDiff: React.FC<SideBySideDiffProps> = ({ oldValue, newValue }) => {
  const oldStr = formatValueForDiff(oldValue);
  const newStr = formatValueForDiff(newValue);
  const diffParts = computeLineDiff(oldStr, newStr);

  return (
    <Box
      sx={{
        display: 'flex',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          flex: 1,
          borderRight: 1,
          borderColor: 'divider',
          backgroundColor: 'grey.50',
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.5,
            backgroundColor: 'grey.100',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Previous
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {diffParts.map((part, idx) => (
            <DiffLine key={idx} part={part} lineNumber={idx} side="left" />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          backgroundColor: 'grey.50',
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.5,
            backgroundColor: 'grey.100',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Current
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {diffParts.map((part, idx) => (
            <DiffLine key={idx} part={part} lineNumber={idx} side="right" />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const DiffAccordion: React.FC<DiffAccordionProps> = ({
  diff,
  defaultExpanded = true,
  expanded,
  onToggle,
}) => {
  const {
    key, oldValue, newValue, hasChanges,
  } = diff;

  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;

  const handleChange = (_event: React.SyntheticEvent, newExpanded: boolean) => {
    if (onToggle) {
      onToggle(key, newExpanded);
    }
    if (!isControlled) {
      setInternalExpanded(newExpanded);
    }
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleChange}
      sx={{
        mb: 2,
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: '6px !important',
        '&.Mui-expanded': {
          margin: '0 0 16px 0',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.100',
          minHeight: '40px',
          '&.Mui-expanded': {
            minHeight: '40px',
          },
          '& .MuiAccordionSummary-content': {
            margin: '8px 0',
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {key}
        </Typography>
        {hasChanges ? (
          <Chip
            label="modified"
            size="small"
            sx={{
              height: '20px',
              fontSize: '11px',
              backgroundColor: DIFF_COLORS.chipModified.bg,
              color: DIFF_COLORS.chipModified.text,
            }}
          />
        ) : (
          <Chip
            label="unchanged"
            size="small"
            sx={{
              height: '20px',
              fontSize: '11px',
              backgroundColor: 'grey.200',
              color: 'text.secondary',
            }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <SideBySideDiff oldValue={oldValue} newValue={newValue} />
      </AccordionDetails>
    </Accordion>
  );
};

export default DiffAccordion;
